import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreateCrmCompanyTaskPayload, CrmTask, TaskQuickFilter, TaskStatus } from "types/crm/task";
import { usePaginatedList } from "../usePaginatedList";
import { companyApi, companyQueryKeys, UpdateCrmCompanyTaskPayload } from "service/crm/api/company";
import { useEntityCreateMutation } from "../useEntityMutation";


export const useCompanyTasks = (
  companyUuid: string,
  pageSize = 20,
  status?: TaskStatus,
  quickFilter?: TaskQuickFilter,
) => {
  const { items, ...rest } = usePaginatedList<CrmTask>(
    companyQueryKeys.tasks(companyUuid, status, quickFilter),
    (page) => companyApi.getCompanyTasks(companyUuid, page, pageSize, status, quickFilter),
    !!companyUuid,
  );
  return { tasks: items, ...rest };
};

/** This company's tasks that are past due and not completed. */
export const useCompanyOverdueTasks = (companyUuid: string, pageSize = 20) =>
  useCompanyTasks(companyUuid, pageSize, undefined, "OVERDUE");

export const useCreateCompanyTaskMutation = () =>
  useEntityCreateMutation<CreateCrmCompanyTaskPayload, CrmTask>(
    (payload) => companyApi.createTask(payload),
    (variables) => [
      [...companyQueryKeys.all, "tasks", variables.companyUuid],
      companyQueryKeys.activities(variables.companyUuid),
    ],
  );

export const useUpdateCompanyTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCrmCompanyTaskPayload) =>
      companyApi.updateTask(payload),
    onSuccess: (_data, variables) => {
      // Prefix-match invalidation: clears the cache for every status filter
      // of this company's task list, not just "ALL".
      queryClient.invalidateQueries({
        queryKey: [...companyQueryKeys.all, "tasks", variables.companyUuid],
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};

export const useDeleteCompanyTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyUuid,
      taskUuid,
    }: {
      companyUuid: string;
      taskUuid: string;
    }) => companyApi.deleteCompanyTask(companyUuid, taskUuid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...companyQueryKeys.all, "tasks", variables.companyUuid],
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};